/**
 * Save an insight to a space: pick one of the spaces you can already see, or
 * name a new one. Built from the UI kit.
 */

import React from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import FolderRounded from '@mui/icons-material/FolderRounded';
import { EmptyState, Modal, SearchField, TilePicker } from '../ui-kit';
import { formatRelativeTime } from '../utils/date';

interface SaveToSpaceModalProps {
  open: boolean;
  onClose: () => void;
  spaces: Array<{
    space_id: string;
    name: string;
    description?: string | null;
    created_at: string;
    query_count: number;
  }>;
  loading: boolean;
  error: string | null;
  selectedSpaceId: string | null;
  setSelectedSpaceId: (v: string | null) => void;
  newSpaceName: string;
  setNewSpaceName: (v: string) => void;
  onSubmit: () => void;
  creating: boolean;
  submitError: string | null;
}

export default function SaveToSpaceModal({
  open,
  onClose,
  spaces,
  loading,
  error,
  selectedSpaceId,
  setSelectedSpaceId,
  newSpaceName,
  setNewSpaceName,
  onSubmit,
  creating,
  submitError,
}: SaveToSpaceModalProps) {
  const [query, setQuery] = React.useState('');
  React.useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const shown = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = q
      ? spaces.filter((s) => `${s.name} ${s.description ?? ''}`.toLowerCase().includes(q))
      : spaces;
    // Newest first: the space someone just made is the one they mean.
    return [...matching].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [spaces, query]);

  // One or the other: picking a space clears the new name, and typing a new
  // name clears the pick, so it is always clear where the insight goes.
  const creatingNew = newSpaceName.trim().length > 0;
  const canSave = creatingNew || Boolean(selectedSpaceId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Save insight to space"
      size="md"
      busy={creating}
      footer={
        <>
          <Button color="inherit" onClick={onClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={!canSave || creating}
            startIcon={creating ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {creating ? 'Saving…' : creatingNew ? 'Create and save' : 'Save'}
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        <Box component="section" aria-labelledby="save-to-space-choose">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
            <Typography variant="subtitle2" component="h3" id="save-to-space-choose">
              Choose a space
            </Typography>
            {spaces.length > 0 && (
              <Box sx={{ width: { xs: '100%', sm: 240 } }}>
                <SearchField value={query} onChange={setQuery} placeholder="Search spaces…" />
              </Box>
            )}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          {loading ? (
            <EmptyState message="Loading spaces…" height={120} />
          ) : (
            <TilePicker
              ariaLabel="Spaces"
              options={shown.map((s) => ({
                value: s.space_id,
                title: s.name,
                description: s.description?.trim() || undefined,
                meta: `Created ${formatRelativeTime(s.created_at)} · ${(s.query_count || 0).toLocaleString()} ${
                  s.query_count === 1 ? 'insight' : 'insights'
                }`,
                icon: <FolderRounded fontSize="small" />,
              }))}
              value={creatingNew ? null : selectedSpaceId}
              onChange={(id) => {
                setSelectedSpaceId(id);
                setNewSpaceName('');
              }}
              maxHeight={300}
              emptyMessage={query.trim() ? `No spaces match “${query.trim()}”.` : 'No spaces yet. Create one below.'}
            />
          )}
        </Box>

        <Box component="section" aria-labelledby="save-to-space-new">
          <Typography variant="subtitle2" component="h3" id="save-to-space-new" sx={{ mb: 1.5 }}>
            Or create a new space
          </Typography>
          <TextField
            label="New space name"
            placeholder="e.g. Executive queries, Marketing analytics"
            fullWidth
            value={newSpaceName}
            onChange={(e) => {
              setNewSpaceName(e.target.value);
              if (e.target.value.trim()) setSelectedSpaceId(null);
            }}
          />
        </Box>

        {submitError && <Alert severity="error">{submitError}</Alert>}
      </Stack>
    </Modal>
  );
}
